import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ticketService } from '../services/ticketService';
import { Ticket, TicketStatus, UserRole } from '../types';
import { useAuth } from '../context/AuthContext';
import { useDateTime } from '../hooks/useDateTime';
import { useIsMobile } from '../hooks/useIsMobile';

const TicketList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatDate } = useDateTime();
  const isMobile = useIsMobile();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    loadTickets();
  }, [statusFilter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      
      const data = await ticketService.getTickets(filters);
      setTickets(data.tickets);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const orphanedCount = tickets.filter((ticket) => !ticket.clientCompany).length;

  const handlePurgeOrphans = async () => {
    if (!window.confirm(`Delete ${orphanedCount} tickets that belong to deleted companies?`)) {
      return;
    }

    try {
      setPurging(true);
      await ticketService.purgeOrphanedTickets();
      await loadTickets();
    } catch (error) {
      console.error('Error cleaning orphaned tickets:', error);
    } finally {
      setPurging(false);
    }
  };

  const statusOptions = Object.values(TicketStatus).map(status => ({
    label: status.replace(/_/g, ' '),
    value: status
  }));

  const statusBodyTemplate = (rowData: Ticket) => {
    const severityMap: any = {
      PENDING_APPROVAL: 'warning',
      REJECTED: 'danger',
      NEW: 'info',
      OPEN: 'info',
      IN_PROGRESS: 'warning',
      PENDING: 'warning',
      RESOLVED: 'success',
      CLOSED: 'secondary',
      CANCELLED: 'danger'
    };

    return <Tag value={rowData.status.replace(/_/g, ' ')} severity={severityMap[rowData.status] || 'info'} />;
  };

  const companyBodyTemplate = (rowData: Ticket) => {
    if (!rowData.clientCompany) {
      return <Tag value="Deleted company" severity="danger" />;
    }
    return rowData.clientCompany.name;
  };

  const dateBodyTemplate = (rowData: Ticket) => formatDate(rowData.createdAt);

  const visibleTickets = tickets.filter((ticket) => {
    if (!globalFilter.trim()) return true;
    const q = globalFilter.trim().toLowerCase();
    return [
      ticket.ticketNumber,
      ticket.title,
      ticket.category,
      ticket.status,
      ticket.clientCompany?.name
    ].some((value) => String(value || '').toLowerCase().includes(q));
  });

  return (
    <div className="ticket-list-container">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Tickets</h1>
          <p className="page-subtitle">Manage all tickets in one place</p>
        </div>
        <div className="page-header-actions">
          {user?.role === UserRole.SUPER_ADMIN && orphanedCount > 0 && (
            <Button
              label={`Remove ${orphanedCount} orphaned`}
              icon="pi pi-trash"
              className="p-button-danger p-button-outlined"
              onClick={handlePurgeOrphans}
              loading={purging}
            />
          )}
          <Button
            label="New Ticket"
            icon="pi pi-plus"
            className="new-ticket-btn"
            onClick={() => navigate('/tickets/new')}
          />
        </div>
      </div>

      {/* Filters Card */}
      <Card className="filters-card">
        <div className="filters-container">
          <div className="search-box">
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                type="search"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search tickets..."
                className="search-input"
              />
            </span>
          </div>
          
          <div className="filter-dropdowns">
            <Dropdown
              value={statusFilter}
              options={statusOptions}
              onChange={(e) => setStatusFilter(e.value)}
              placeholder="Status"
              showClear
              className="filter-dropdown"
            />
          </div>
        </div>
      </Card>

      {isMobile ? (
        <div className="list-cards">
          {visibleTickets.map((ticket) => (
            <div
              key={ticket._id}
              className="list-card clickable"
              onClick={() => navigate(`/tickets/${ticket._id}`)}
            >
              <div className="list-card-header">
                <div>
                  <div className="list-card-title">{ticket.title}</div>
                  <div className="list-card-sub">{ticket.ticketNumber}</div>
                </div>
                {statusBodyTemplate(ticket)}
              </div>
              <div className="list-card-meta">
                <span>{String(ticket.category || '').replace(/_/g, ' ')}</span>
                <span>{ticket.clientCompany ? ticket.clientCompany.name : 'Deleted company'}</span>
                <span>{formatDate(ticket.createdAt)}</span>
              </div>
            </div>
          ))}
          {!loading && visibleTickets.length === 0 && (
            <div className="mobile-empty-state">
              <i className="pi pi-ticket" />
              <h3>No tickets</h3>
              <p>Nothing matches this search.</p>
            </div>
          )}
        </div>
      ) : (
      <Card className="table-card">
        <DataTable
          value={tickets}
          loading={loading}
          globalFilter={globalFilter}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onRowClick={(e) => navigate(`/tickets/${e.data._id}`)}
          className="tickets-table"
          emptyMessage="No tickets to display"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} tickets"
        >
          <Column 
            field="ticketNumber" 
            header="Ticket Number" 
            sortable 
            style={{ minWidth: '120px' }}
          />
          <Column 
            field="title" 
            header="Title" 
            sortable 
            style={{ minWidth: '250px' }}
          />
          <Column 
            field="category" 
            header="Category" 
            sortable 
            style={{ minWidth: '200px' }}
          />
          <Column 
            field="status" 
            header="Status" 
            body={statusBodyTemplate} 
            sortable 
            style={{ minWidth: '180px' }}
          />
          <Column
            header="Company"
            body={companyBodyTemplate}
            style={{ minWidth: '160px' }}
          />
          <Column 
            field="createdAt" 
            header="Created" 
            body={dateBodyTemplate} 
            sortable 
            style={{ minWidth: '120px' }}
          />
        </DataTable>
      </Card>
      )}

      <style>{`
        .ticket-list-container {
          animation: fadeIn 0.4s ease;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-header-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0.5rem 0 0 0;
        }

        .new-ticket-btn {
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
          transition: all 0.2s ease;
        }

        .new-ticket-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3);
        }

        .filters-card {
          margin-bottom: 1.5rem;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .filters-card .p-card-body {
          padding: 1.5rem;
        }

        .filters-container {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: min(100%, 250px);
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border-radius: 12px;
          border: 2px solid #e5e7eb;
          transition: all 0.2s ease;
        }

        .search-input:hover {
          border-color: #10b981;
        }

        .search-input:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .filter-dropdowns {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .filter-dropdown {
          min-width: 150px;
        }

        .filter-dropdown .p-dropdown {
          border-radius: 12px;
          border: 2px solid #e5e7eb;
          transition: all 0.2s ease;
        }

        .filter-dropdown .p-dropdown:hover {
          border-color: #10b981;
        }

        .table-card {
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .table-card .p-card-body {
          padding: 0;
        }

        .tickets-table {
          border-radius: 16px;
        }

        .tickets-table .p-datatable-header {
          background: transparent;
          border: none;
          padding: 1.5rem;
        }

        .tickets-table .p-datatable-thead > tr > th {
          background: #f9fafb;
          border-color: #e5e7eb;
          padding: 1rem;
          font-weight: 700;
          color: #374151;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .tickets-table .p-datatable-tbody > tr {
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .tickets-table .p-datatable-tbody > tr > td {
          padding: 1rem;
          border-color: #f3f4f6;
        }

        .tickets-table .p-datatable-tbody > tr:hover {
          background: #f0fdf4 !important;
          transform: scale(1.005);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
        }

        .tickets-table .p-paginator {
          border: none;
          background: #f9fafb;
          padding: 1rem 1.5rem;
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }

        .tickets-table .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-color: #10b981;
        }

        .tickets-table .p-tag {
          padding: 0.35rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 600;
          border-radius: 8px;
        }

        .tickets-table .p-datatable-emptymessage > td {
          text-align: center;
          padding: 3rem;
          color: #9ca3af;
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .page-header .new-ticket-btn {
            width: 100%;
            justify-content: center;
          }

          .filters-container {
            flex-direction: column;
          }

          .search-box,
          .search-box .p-input-icon-left,
          .search-input,
          .filter-dropdowns,
          .filter-dropdown {
            width: 100%;
          }

          .tickets-table .p-datatable-tbody > tr:hover {
            transform: none;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default TicketList; 